import { useEffect, useMemo, useState } from 'react';
import { Button, Empty, Input, Popover, Spin, Tooltip, Typography } from 'antd';
import { SmileOutlined } from '@ant-design/icons';
import { palette } from '../app/theme';

interface Emoji {
  unicode: string;
  label: string;
  tags: string[];
  group: number;
}

/** emojibase groups, without "component" (2: bare skin tones and hair parts) */
const GROUPS: { id: number; icon: string; label: string }[] = [
  { id: 0, icon: '😀', label: 'Smileys & emotion' },
  { id: 1, icon: '👋', label: 'People & body' },
  { id: 3, icon: '🐻', label: 'Animals & nature' },
  { id: 4, icon: '🍔', label: 'Food & drink' },
  { id: 5, icon: '✈️', label: 'Travel & places' },
  { id: 6, icon: '⚽', label: 'Activities' },
  { id: 7, icon: '💡', label: 'Objects' },
  { id: 8, icon: '❤️', label: 'Symbols' },
  { id: 9, icon: '🏁', label: 'Flags' },
];
const RECENT_KEY = 'crm.recent-emojis';
const RECENT_MAX = 24;

let cache: Promise<Emoji[]> | null = null;
/** The emoji list (~570 KB) loads the first time a picker opens, not with the app. */
function loadEmojis() {
  cache ??= import('emojibase-data/en/compact.json').then((m) =>
    (m.default as { unicode: string; label: string; tags?: string[]; group?: number; order?: number }[])
      .filter((e) => e.group !== undefined && e.group !== 2)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((e) => ({ unicode: e.unicode, label: e.label, tags: e.tags ?? [], group: e.group as number })),
  );
  cache.catch(() => (cache = null));
  return cache;
}

const readRecent = (): string[] => {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]') as string[];
  } catch {
    return [];
  }
};

/** Smiley button that opens a searchable emoji list; `onPick` receives the emoji character. */
export function EmojiPicker({ onPick, label = 'Insert emoji' }: { onPick: (emoji: string) => void; label?: string }) {
  const [open, setOpen] = useState(false);
  const [emojis, setEmojis] = useState<Emoji[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [q, setQ] = useState('');
  const [group, setGroup] = useState(0);
  const [recent, setRecent] = useState<string[]>(readRecent);

  useEffect(() => {
    if (!open || emojis) return;
    loadEmojis().then(setEmojis, () => setFailed(true));
  }, [open, emojis]);

  const shown = useMemo(() => {
    if (!emojis) return [];
    const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!words.length) return emojis.filter((e) => e.group === group);
    // every word must start a word of the name or a tag: "red heart", "party", "thumb"
    return emojis.filter((e) => {
      const hay = [e.label.toLowerCase(), ...e.tags].join(' ').split(/[\s:,-]+/);
      return words.every((w) => hay.some((h) => h.startsWith(w)));
    });
  }, [emojis, q, group]);

  const pick = (emoji: string) => {
    onPick(emoji);
    const next = [emoji, ...recent.filter((r) => r !== emoji)].slice(0, RECENT_MAX);
    setRecent(next);
    try {
      localStorage.setItem(RECENT_KEY, JSON.stringify(next));
    } catch {
      /* private mode: recents just aren't kept */
    }
  };

  const grid = (list: { unicode: string; label: string }[]) => (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(9, 1fr)', gap: 2 }}>
      {list.map((e) => (
        <button key={e.unicode} type="button" className="emoji-cell" title={e.label} aria-label={e.label} onClick={() => pick(e.unicode)}>
          {e.unicode}
        </button>
      ))}
    </div>
  );

  const byChar = useMemo(() => new Map((emojis ?? []).map((e) => [e.unicode, e])), [emojis]);
  const content = (
    <div style={{ width: 340 }}>
      <Input.Search autoFocus allowClear aria-label="Search emojis" placeholder="Search emojis (e.g. party, heart, rocket)" value={q} onChange={(e) => setQ(e.target.value)} />
      {!q && (
        <div role="tablist" aria-label="Emoji categories" style={{ display: 'flex', justifyContent: 'space-between', margin: '8px 0 4px', borderBottom: `1px solid ${palette.border}` }}>
          {GROUPS.map((g) => (
            <Tooltip key={g.id} title={g.label} mouseEnterDelay={0.4}>
              <button type="button" role="tab" aria-selected={group === g.id} aria-label={g.label} className={`emoji-tab${group === g.id ? ' is-active' : ''}`} onClick={() => setGroup(g.id)}>
                {g.icon}
              </button>
            </Tooltip>
          ))}
        </div>
      )}
      <div style={{ height: 260, overflowY: 'auto', marginTop: q ? 8 : 0 }}>
        {failed ? (
          <Typography.Text type="danger">Could not load emojis. Close and try again.</Typography.Text>
        ) : !emojis ? (
          <div style={{ display: 'grid', placeItems: 'center', height: '100%' }}><Spin /></div>
        ) : (
          <>
            {!q && recent.length > 0 && group === 0 && (
              <>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>Recently used</Typography.Text>
                {grid(recent.map((u) => ({ unicode: u, label: byChar.get(u)?.label ?? u })))}
                <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 6 }}>{GROUPS[0].label}</Typography.Text>
              </>
            )}
            {shown.length ? grid(shown) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={`No emoji matches "${q}"`} />}
          </>
        )}
      </div>
    </div>
  );

  return (
    <Popover open={open} onOpenChange={setOpen} trigger="click" placement="bottomRight" content={content} arrow={false} destroyOnHidden>
      <Tooltip title={open ? undefined : label}>
        <Button type="text" size="small" icon={<SmileOutlined />} aria-label={label} onMouseDown={(e) => e.preventDefault()} />
      </Tooltip>
    </Popover>
  );
}
